import React, { useEffect, useState, useCallback } from 'react';
import { Eye, EyeOff, Camera, MapPin, Sparkles, Wand2, Check, CheckCircle, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabaseClient';
import InterestSelector from './InterestSelector';
import { generateBio, getPersonalityTemplates, PersonalityType } from '../utils/bioGenerator';
import { validateUsername } from '../utils/profanityFilter';
import { generateCleanUsername } from '../utils/usernameGenerator';
import { uploadAvatarToStorage } from '../services/userService';
import ImageCropper from './ImageCropper';
import { trackEvent } from '../services/analyticsService';
import { trackRegistrationStarted, trackRegistrationCompleted } from '../utils/metaPixel';
import { getReferralData } from '../utils/referralTracking';
import WelcomeModal from './WelcomeModal';

interface ProfileBuilderNewProps {
  onComplete: () => void;
  onLoginClick?: () => void;
  fromVybe?: boolean;
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const usernameRegex = /^[a-zA-Z0-9_-]{3,}$/;

const ProfileBuilderNew: React.FC<ProfileBuilderNewProps> = ({ onComplete, onLoginClick, fromVybe = false }) => {
  const { register, updateUser, currentUser, requestAndStoreCurrentLocation, refreshCurrentUser } = useAuth();

  // Get referral code from URL
  const [referralCode, setReferralCode] = useState<string | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      setReferralCode(ref);
    }
  }, []);

  // Lock page scroll
  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, []);

  // Main steps: 0 = Create Account, 1 = Your Interests, 2 = Review & Launch
  const [mainStep, setMainStep] = useState(0);

  // Sub-steps within each main step
  // Create Account: 0=email, 1=username, 2=password, 3=name&dob
  // Your Interests: 0=interests, 1=photo
  // Review & Launch: 0=review (only one)
  const [subStep, setSubStep] = useState(0);

  const [form, setForm] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    dateOfBirth: '',
    gender: '' as 'male' | 'female' | 'non-binary' | 'prefer-not-to-say' | '',
    bio: '',
    interests: [] as string[],
    avatarUrl: null as string | null,
    location: '' as string,
    latitude: null as number | null,
    longitude: null as number | null,
    referredBy: null as string | null,
  });

  // Separate state for date parts to allow independent selection
  const [dobDay, setDobDay] = useState('');
  const [dobMonth, setDobMonth] = useState('');
  const [dobYear, setDobYear] = useState('');

  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [selectedPersonality, setSelectedPersonality] = useState<PersonalityType>('social');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [showImageCropper, setShowImageCropper] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Session ID for deduplication of abandonment tracking
  const [sessionId] = useState(() => `session-${Date.now()}-${Math.random().toString(36).substring(7)}`);

  // Username availability
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'ok' | 'taken' | 'profane'>('idle');
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!form.username || !usernameRegex.test(form.username)) {
        setUsernameStatus('idle');
        return;
      }

      // Check for profanity first
      const profanityError = validateUsername(form.username);
      if (profanityError && profanityError.includes('inappropriate')) {
        setUsernameStatus('profane');
        return;
      }

      setUsernameStatus('checking');
      const { data, error } = await supabase
        .from('profiles')
        .select('id')
        .eq('username', form.username)
        .limit(1);
      if (cancelled) return;
      if (error) return setUsernameStatus('idle');
      setUsernameStatus(data && data.length > 0 ? 'taken' : 'ok');
    };
    const t = setTimeout(run, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [form.username]);

  // Track abandoned registration - ONLY when user clicks Next from email step
  const [hasTrackedEmail, setHasTrackedEmail] = useState(false);

  const trackAbandonment = useCallback((email: string, stepReached: string) => {
    // Don't track if already tracked this email
    if (hasTrackedEmail) return;

    // Don't track if no email
    if (!email) return;

    // CRITICAL: Only track valid complete email addresses
    // Reject partial emails like "b", "bl", "hb", etc.
    if (!emailRegex.test(email)) {
      console.log('[ProfileBuilderNew] Invalid email, skipping abandonment tracking:', email);
      return;
    }

    // Fire and forget - don't block user
    try {
      const referralData = getReferralData();
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseKey) return;

      fetch(`${supabaseUrl}/functions/v1/save-abandoned-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseKey}`,
        },
        body: JSON.stringify({
          email: email,
          partialData: {
            username: form.username || null,
            fullName: form.fullName || null,
            mainStep,
            subStep,
          },
          utmSource: referralData?.referral_source || null,
          utmMedium: referralData?.referral_medium || null,
          utmCampaign: referralData?.referral_campaign || null,
          referralCode: referralCode || null,
          stepReached: stepReached,
          sessionId,
        }),
      }).then(() => {
        setHasTrackedEmail(true);
      }).catch((err) => {
        console.log('[ProfileBuilderNew] Abandonment tracking failed (non-critical):', err);
      });
    } catch (err) {
      console.log('[ProfileBuilderNew] Abandonment tracking error (non-critical):', err);
    }
  }, [form.username, form.fullName, mainStep, subStep, referralCode, sessionId, hasTrackedEmail]);

  const setField = useCallback(<K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  }, []);

  // Sync date parts when all are filled
  useEffect(() => {
    if (dobDay && dobMonth && dobYear) {
      const fullDate = `${dobYear}-${dobMonth}-${dobDay}`;
      if (fullDate !== form.dateOfBirth) {
        setField('dateOfBirth', fullDate);
      }
    }
  }, [dobDay, dobMonth, dobYear]);

  // Calculate age
  const calculateAge = (dob: string): number | undefined => {
    if (!dob || !dob.match(/^\d{4}-\d{2}-\d{2}$/)) return undefined;
    const today = new Date();
    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return undefined;
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  };

  // Debug: log mainStep changes
  useEffect(() => {
    console.log('[ProfileBuilderNew] State changed - mainStep:', mainStep, 'subStep:', subStep);
  }, [mainStep, subStep]);

  // Auto-generate bio when interests are set and we reach review step
  useEffect(() => {
    if (mainStep === 2 && form.interests.length > 0 && !form.bio) {
      try {
        const age = calculateAge(form.dateOfBirth);
        const generatedBio = generateBio(selectedPersonality, {
          age,
          interests: form.interests,
        });
        setField('bio', generatedBio);
      } catch (err) {
        console.error('Bio generation error:', err);
        setField('bio', `I love ${form.interests.slice(0, 3).join(', ')} and meeting new people!`);
      }
    }
  }, [mainStep, form.interests, form.dateOfBirth, form.location, selectedPersonality]);

  // Regenerate bio when personality changes
  const handlePersonalityChange = (personality: PersonalityType) => {
    setSelectedPersonality(personality);
    const age = calculateAge(form.dateOfBirth);
    const generatedBio = generateBio(personality, {
      age,
      interests: form.interests,
    });
    setField('bio', generatedBio);
  };

  // Regenerate bio with current personality (for "Try Again" button)
  const handleRegenerateBio = () => {
    const age = calculateAge(form.dateOfBirth);
    const generatedBio = generateBio(selectedPersonality, {
      age,
      interests: form.interests,
    });
    setField('bio', generatedBio);
  };

  // Generate a clean username with profanity filtering and availability check
  const handleGenerateUsername = async () => {
    setIsGenerating(true);
    setError('');

    try {
      // Try up to 10 times to find a clean, available username
      for (let attempt = 0; attempt < 10; attempt++) {
        // Generate a clean username (already filtered for profanity)
        const username = generateCleanUsername(5); // 5 internal retries for profanity

        if (!username) {
          // Failed to generate clean username
          continue;
        }

        // Check availability in database
        const { data, error } = await supabase
          .from('profiles')
          .select('id')
          .eq('username', username)
          .limit(1);

        if (error) {
          console.error('Error checking username availability:', error);
          continue;
        }

        // If username is available, use it
        if (!data || data.length === 0) {
          setField('username', username);
          setIsGenerating(false);
          return;
        }

        // Username taken, try again
        console.log(`Generated username "${username}" is taken, retrying...`);
      }

      // All attempts failed
      setError('Could not generate an available username. Please try again or enter your own.');
    } catch (err) {
      console.error('Error generating username:', err);
      setError('Failed to generate username. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const onFile: React.ChangeEventHandler<HTMLInputElement> = (ev) => {
    console.log('onFile triggered', ev.target.files);
    const f = ev.target.files?.[0];
    if (!f) {
      console.log('No file selected');
      return;
    }
    console.log('File selected:', f.name, f.type, f.size);

    setSelectedImageFile(f);
    setShowImageCropper(true);

    ev.target.value = '';
  };

  const handleCropComplete = (croppedBlob: Blob, croppedUrl: string) => {
    const croppedFile = new File([croppedBlob], selectedImageFile?.name || 'avatar.jpg', {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });

    setAvatarFile(croppedFile);
    setField('avatarUrl', croppedUrl);
    setShowImageCropper(false);
    setSelectedImageFile(null);
  };

  const handleCropCancel = () => {
    setShowImageCropper(false);
    setSelectedImageFile(null);
  };

  const handleUseMyLocation = async () => {
    try {
      // Get location directly during onboarding (user isn't authenticated yet)
      if (!navigator.geolocation) {
        setError('Geolocation is not supported by your browser');
        return;
      }

      setSubmitting(true);
      setError('');

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const { latitude, longitude } = position.coords;

            // Get city name from Google Maps API
            const response = await fetch(
              `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY}`
            );
            const data = await response.json();

            let locationName = 'Your current location';
            if (data.results && data.results.length > 0) {
              // Try to get city, state format
              const addressComponents = data.results[0].address_components;
              const city = addressComponents.find((c: any) => c.types.includes('locality'))?.long_name;
              const state = addressComponents.find((c: any) => c.types.includes('administrative_area_level_1'))?.short_name;

              if (city && state) {
                locationName = `${city}, ${state}`;
              } else {
                locationName = data.results[0].formatted_address;
              }
            }

            // Store in form state for later when we create the account
            setField('location', locationName);
            setField('latitude', latitude);
            setField('longitude', longitude);
            setSubmitting(false);
          } catch (err) {
            console.error('Error getting location name:', err);
            setError('Could not get your location. You can skip this step.');
            setSubmitting(false);
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          setError('Could not get your location. You can skip this step.');
          setSubmitting(false);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } catch (err) {
      console.error('Location error:', err);
      setError('Could not get your location. You can skip this step.');
      setSubmitting(false);
    }
  };

  // Validation for current sub-step
  const canProceed = (): boolean => {
    setError('');
    setFieldErrors({});
    const errors: Record<string, string> = {};

    if (mainStep === 0) {
      if (subStep === 0) { // Email
        if (!emailRegex.test(form.email)) {
          errors.email = 'Enter a valid email';
        }
      }
      if (subStep === 1) { // Username
        if (!usernameRegex.test(form.username)) {
          errors.username = 'Username must be 3+ characters: letters, numbers, _ or -';
        }
        if (usernameStatus === 'profane') {
          errors.username = 'Username contains inappropriate language';
        }
        if (usernameStatus === 'taken') {
          errors.username = 'That username is taken';
        }
        if (usernameStatus === 'checking') {
          // Don't set error for checking state, just return false
          return false;
        }
        if (usernameStatus !== 'ok' && !errors.username) {
          errors.username = 'Please wait for username validation';
        }
      }
      if (subStep === 2) { // Password
        if (form.password.length < 8) {
          errors.password = 'Password must be at least 8 characters';
        }
        if (form.password !== form.confirmPassword) {
          errors.confirmPassword = 'Passwords do not match';
        }
      }
      if (subStep === 3) { // Name & DOB
        if (!form.fullName || !form.fullName.trim()) {
          errors.fullName = 'Full name is required';
        }
        if (!form.dateOfBirth || !form.dateOfBirth.match(/^\d{4}-\d{2}-\d{2}$/)) {
          errors.dateOfBirth = 'Please select your complete date of birth';
        } else {
          const age = calculateAge(form.dateOfBirth);
          if (!age || age < 18) {
            errors.dateOfBirth = 'You must be 18 or older to join';
          }
        }
        if (!form.gender) {
          errors.gender = 'Please select your gender';
        }
      }
    }

    if (mainStep === 1) {
      if (subStep === 0) { // Interests
        if (form.interests.length < 3) {
          errors.interests = 'Pick at least 3 interests';
        }
      }
      // Photo is skippable
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }

    return true;
  };

  const handleNext = async () => {
    console.log('[ProfileBuilderNew] handleNext called. mainStep:', mainStep, 'subStep:', subStep);
    if (!canProceed()) return;

    // Navigate sub-steps
    if (mainStep === 0 && subStep < 3) {
      // Track registration_started when user enters email and clicks Continue
      if (subStep === 0) {
        trackEvent('registration_started', { email: form.email, username: form.username });
        trackRegistrationStarted({ email: form.email, username: form.username });
        // Track abandoned registration with email (once only)
        trackAbandonment(form.email, 'email_entered');
      }
      console.log('[ProfileBuilderNew] Moving to next sub-step:', subStep + 1);
      setSubStep(subStep + 1);
    } else if (mainStep === 0 && subStep === 3) {
      // Just move to interests step - don't create account yet
      console.log('[ProfileBuilderNew] Moving to interests step');
      setMainStep(1);
      setSubStep(0);
      return;
    } else if (mainStep === 1 && subStep < 1) {
      console.log('[ProfileBuilderNew] Interests flow - moving to sub-step:', subStep + 1);
      setSubStep(subStep + 1);
    } else if (mainStep === 1 && subStep === 1) {
      // Just move to review - don't save yet
      console.log('[ProfileBuilderNew] Moving to review step (mainStep=2)');
      setMainStep(2);
      setSubStep(0);
    } else if (mainStep === 2) {
      // Final submission - NOW create the account with ALL data
      try {
        setSubmitting(true);
        console.log('[ProfileBuilderNew] Creating account with all data...');
        console.log('[ProfileBuilderNew] Avatar file state:', avatarFile ? { name: avatarFile.name, size: avatarFile.size } : 'No file');

        // Create account with all collected data
        const result = await register(form.email, form.password, form.username, {
          fullName: form.fullName,
          dateOfBirth: form.dateOfBirth,
          gender: form.gender,
          phone: '',
        });

        if (!result.success) throw new Error('Registration failed');

        console.log('[ProfileBuilderNew] Account created with session, now updating profile...');

        // Upload avatar if user selected one
        let uploadedAvatarUrl: string | null = null;
        if (avatarFile) {
          console.log('[ProfileBuilderNew] Uploading avatar to storage...', { fileName: avatarFile.name, fileSize: avatarFile.size });
          try {
            uploadedAvatarUrl = await uploadAvatarToStorage(avatarFile);
            if (uploadedAvatarUrl) {
              console.log('[ProfileBuilderNew] Avatar uploaded successfully:', uploadedAvatarUrl);
            } else {
              console.warn('[ProfileBuilderNew] Avatar upload returned null, continuing without photo');
            }
          } catch (uploadErr) {
            console.error('[ProfileBuilderNew] Avatar upload failed:', uploadErr);
            // Don't fail the entire signup if photo upload fails
            // Just continue without the photo
          }
        } else {
          console.log('[ProfileBuilderNew] No avatar file selected, skipping upload');
        }

        // Now update with interests, photo, location, bio, and referral
        const { data: profile } = await supabase.auth.getUser();
        if (profile.user) {
          const updateData: any = {
            id: profile.user.id,
            interests: form.interests,
            location: form.location || undefined,
            latitude: form.latitude || undefined,
            longitude: form.longitude || undefined,
            bio: form.bio,
            avatarUrl: uploadedAvatarUrl || undefined,
          };

          // If there's a referral code, store it
          if (referralCode) {
            updateData.referredBy = referralCode;
          }

          // CRITICAL: Log what we're actually sending
          console.log('[ONBOARDING_SUBMIT] userId', profile.user.id);
          console.log('[ONBOARDING_SUBMIT] interestsCount', form.interests?.length ?? 0, 'bioLen', form.bio?.length ?? 0, 'hasAvatar', !!uploadedAvatarUrl);
          console.log('[ONBOARDING_SUBMIT] payloadKeys', Object.keys(updateData));

          const updateSuccess = await updateUser(updateData);

          console.log('[ONBOARDING_SUBMIT] result', { success: updateSuccess });

          if (!updateSuccess) {
            console.error('[ONBOARDING_SUBMIT] FAILED - profile update returned false');
            throw new Error('Failed to save profile data');
          }

          // Process referral rewards if there's a referral code
          if (referralCode) {
            try {
              await supabase.functions.invoke('process-referral', {
                body: { referredUserId: profile.user.id, referralCode }
              });
            } catch (referralErr) {
              console.error('[ProfileBuilderNew] Referral processing error:', referralErr);
              // Don't fail signup if referral processing fails
            }
          }

          console.log('[ProfileBuilderNew] Profile complete!');

          // Track successful registration completion
          trackEvent('registration_completed', { user_id: profile.user.id }, profile.user.id);
          trackRegistrationCompleted({
            userId: profile.user.id,
            email: form.email,
            username: form.username
          });

          // Notify admin of new signup and add user to Brevo for marketing (non-blocking)
          const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
          const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

          if (supabaseUrl && supabaseKey) {
            fetch(`${supabaseUrl}/functions/v1/notify-admin-new-signup`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${supabaseKey}`,
              },
              body: JSON.stringify({
                userId: profile.user.id,
                username: form.username,
                email: form.email,
              }),
            }).then(() => {
              console.log('[ProfileBuilderNew] Admin notification sent');
            }).catch((notifyErr) => {
              console.error('[ProfileBuilderNew] Failed to notify admin:', notifyErr);
            });
          }
        }

        // Show welcome modal instead of immediately completing
        setRegistrationComplete(true);
        setShowWelcomeModal(true);
      } catch (err: any) {
        console.error('[ProfileBuilderNew] Launch error:', err);
        setError(err?.message || 'Failed to launch profile');

        // Track registration failure
        trackEvent('registration_failed', {
          error: err?.message || 'Unknown error',
          step: 'launch',
          email: form.email
        });
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handleBack = () => {
    if (subStep > 0) {
      setSubStep(subStep - 1);
    } else if (mainStep > 0) {
      setMainStep(mainStep - 1);
      // Go to last sub-step of previous main step
      if (mainStep === 1) setSubStep(3);
      if (mainStep === 2) setSubStep(1);
    }
  };

  const handleSkip = () => {
    // Only for skippable steps (photo)
    if (mainStep === 1 && subStep === 1) {
      // Move to review
      setMainStep(2);
      setSubStep(0);
    }
  };

  const handleWelcomeModalClose = () => {
    setShowWelcomeModal(false);
    // Now complete the registration flow
    onComplete();
  };

  const mainSteps = ['Create Account', 'Your Interests', 'Review & Launch'];
  const subStepCounts = [4, 2, 1];
  const currentSubStepTotal = subStepCounts[mainStep];
  const totalSubStepProgress = `${subStep + 1}/${currentSubStepTotal}`;

  const personalityTemplates = getPersonalityTemplates();

  return (
    <>
      {showImageCropper && selectedImageFile && (
        <ImageCropper
          imageFile={selectedImageFile}
          onCancel={handleCropCancel}
          onCropComplete={handleCropComplete}
        />
      )}

      <div className="w-full h-full grid grid-rows-[auto,1fr,auto] bg-theme-cards overflow-hidden">
      {/* Header */}
      <div className="px-3 sm:px-5 pt-3 sm:pt-5 pb-2 sm:pb-3">
        <div className="flex items-center justify-between mb-3">
          {mainSteps.map((step, idx) => (
            <div key={idx} className="flex flex-col items-center flex-1">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                  idx < mainStep
                    ? 'bg-accent2 text-white'
                    : idx === mainStep
                    ? 'bg-accent2 text-white'
                    : 'bg-primary-200 text-primary-500'
                }`}
              >
                {idx + 1}
              </div>
              <span className="text-[9px] sm:text-[10px] mt-1 text-white/70 text-center line-clamp-1">
                {step}
              </span>
            </div>
          ))}
        </div>
        <div className="text-right text-xs text-white/60">{totalSubStepProgress}</div>
      </div>

      {/* Content */}
      <div className="px-3 sm:px-5 overflow-y-auto overflow-x-visible pb-20 sm:pb-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${mainStep}-${subStep}`}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
            className="space-y-3 sm:space-y-4"
          >
            {/* Main Step 0: Create Account */}
            {mainStep === 0 && subStep === 0 && (
              <div className="space-y-3">
                <div>
                  {fromVybe ? (
                    <>
                      <h2 className="text-xl sm:text-2xl font-bold mb-2 text-white">Join this Vybe</h2>
                      <p className="text-sm text-white/90 font-medium mb-3">Free to join. You're always in control.</p>
                      <ul className="space-y-2 text-sm text-white/85">
                        <li className="flex items-start gap-2">
                          <span className="text-accent2 mt-0.5">•</span>
                          <span>No payment required</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-accent2 mt-0.5">•</span>
                          <span>Not a dating app</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-accent2 mt-0.5">•</span>
                          <span>Join or leave any Vybe freely</span>
                        </li>
                      </ul>
                    </>
                  ) : (
                    <>
                      <h2 className="text-xl sm:text-2xl font-bold mb-1 text-white">Create Your Profile</h2>
                      <p className="text-xs text-white/70 mb-1">Welcome to Vybe Tribe! Let's get you set up.</p>
                      <p className="text-xs text-white/90 font-medium">
                        The boring part, but really important! We need this to keep you safe and connected with real Tribe members.
                      </p>
                    </>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 text-white">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setField('email', e.target.value)}
                    placeholder="you@example.com"
                    autoFocus
                    className={`w-full p-3 bg-primary-100 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-accent2 focus:outline-none text-sm ${
                      fieldErrors.email ? 'border-2 border-red-500' : 'border border-primary-200'
                    }`}
                  />
                  {fieldErrors.email && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{fieldErrors.email}</p>
                  )}
                </div>
              </div>
            )}

            {mainStep === 0 && subStep === 1 && (
              <div className="space-y-3">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-1 text-white">Choose a username</h2>
                  <p className="text-xs text-white/70">📝 This is how other members will know you. Make it memorable!</p>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 text-white">Username</label>
                  <input
                    value={form.username}
                    onChange={e => setField('username', e.target.value.trim())}
                    placeholder="choose a username"
                    autoFocus
                    className={`w-full p-3 bg-primary-100 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-accent2 focus:outline-none text-sm ${
                      fieldErrors.username ? 'border-2 border-red-500' : 'border border-primary-200'
                    }`}
                  />

                  {/* Generate Username Button */}
                  <button
                    type="button"
                    onClick={handleGenerateUsername}
                    disabled={isGenerating || usernameStatus === 'checking'}
                    className="mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-200 hover:bg-primary-300 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
                  >
                    <Wand2 size={16} className={isGenerating ? 'animate-spin' : ''} />
                    {isGenerating ? 'Generating...' : 'Generate Username for Me'}
                  </button>

                  <div className="mt-1 text-xs">
                    {usernameStatus === 'checking' && <span className="text-white/70">Checking…</span>}
                    {usernameStatus === 'ok' && <span className="text-green-500">Great — that username is available!</span>}
                    {usernameStatus === 'taken' && <span className="text-red-400">That one's taken</span>}
                    {usernameStatus === 'profane' && <span className="text-red-400">Username contains inappropriate language</span>}
                    {fieldErrors.username && <span className="text-red-400">{fieldErrors.username}</span>}
                  </div>
                </div>
              </div>
            )}

            {mainStep === 0 && subStep === 2 && (
              <div className="space-y-3">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-1 text-white">Create a password</h2>
                  <p className="text-xs text-white/70">🔒 Keep your account secure! We've got your back.</p>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 text-white">Password</label>
                  <div className="relative mb-3">
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={form.password}
                      onChange={e => setField('password', e.target.value)}
                      placeholder="Password"
                      autoFocus
                      className="w-full p-3 pr-12 bg-primary-100 border border-primary-200 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-accent2 focus:outline-none text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60"
                    >
                      {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 text-white">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPwd ? 'text' : 'password'}
                      value={form.confirmPassword}
                      onChange={e => setField('confirmPassword', e.target.value)}
                      placeholder="Confirm password"
                      className="w-full p-3 pr-12 bg-primary-100 border border-primary-200 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-accent2 focus:outline-none text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPwd(!showConfirmPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60"
                    >
                      {showConfirmPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <div className="mt-1 text-xs">
                  {form.password && form.confirmPassword && (
                    <p className={`text-xs font-medium ${
                      form.password === form.confirmPassword
                        ? 'text-green-400'
                        : 'text-red-400'
                    }`}>
                      {form.password === form.confirmPassword
                        ? '✓ Passwords match'
                        : '✗ Passwords do not match'
                      }
                    </p>
                  )}
                    {(!form.confirmPassword || form.password !== form.confirmPassword) && (
                      <p className="text-xs text-white/60">Password must be at least 8 characters</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {mainStep === 0 && subStep === 3 && (
              <div className="space-y-3">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-1 text-white">Tell us about you</h2>
                  <p className="text-xs text-white/70">✨ Almost done with the basics! Just a couple more details.</p>
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 text-white">Full name</label>
                  <input
                    value={form.fullName}
                    onChange={e => setField('fullName', e.target.value)}
                    placeholder="Your full name"
                    autoFocus
                    className={`w-full p-3 bg-primary-100 rounded-lg text-white placeholder-white/50 focus:ring-2 focus:ring-accent2 focus:outline-none text-sm ${
                      fieldErrors.fullName ? 'border-2 border-red-500' : 'border border-primary-200'
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 text-white">Date of Birth</label>
                  <p className="text-xs text-white/70 mb-1.5">You must be 18 or older to join</p>
                  <div className="grid grid-cols-3 gap-2 mb-1">
                    <select
                      value={dobDay}
                      onChange={e => setDobDay(e.target.value)}
                      className={`p-2.5 bg-primary-100 rounded-lg text-white focus:ring-2 focus:ring-accent2 focus:outline-none text-sm cursor-pointer ${
                        fieldErrors.dateOfBirth ? 'border-2 border-red-500' : 'border-2 border-primary-200'
                      }`}
                    >
                    <option value="">Day</option>
                    {Array.from({ length: 31 }, (_, i) => {
                      const day = String(i + 1).padStart(2, '0');
                      return <option key={day} value={day}>{i + 1}</option>;
                    })}
                    </select>

                    <select
                      value={dobMonth}
                      onChange={e => setDobMonth(e.target.value)}
                      className={`p-2.5 bg-primary-100 rounded-lg text-white focus:ring-2 focus:ring-accent2 focus:outline-none text-sm cursor-pointer ${
                        fieldErrors.dateOfBirth ? 'border-2 border-red-500' : 'border-2 border-primary-200'
                      }`}
                    >
                    <option value="">Month</option>
                    {[
                      { value: '01', label: 'January' },
                      { value: '02', label: 'February' },
                      { value: '03', label: 'March' },
                      { value: '04', label: 'April' },
                      { value: '05', label: 'May' },
                      { value: '06', label: 'June' },
                      { value: '07', label: 'July' },
                      { value: '08', label: 'August' },
                      { value: '09', label: 'September' },
                      { value: '10', label: 'October' },
                      { value: '11', label: 'November' },
                      { value: '12', label: 'December' },
                    ].map(m => (
                      <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                    </select>

                    <select
                      value={dobYear}
                      onChange={e => setDobYear(e.target.value)}
                      className={`p-2.5 bg-primary-100 rounded-lg text-white focus:ring-2 focus:ring-accent2 focus:outline-none text-sm cursor-pointer ${
                        fieldErrors.dateOfBirth ? 'border-2 border-red-500' : 'border-2 border-primary-200'
                      }`}
                    >
                    <option value="">Year</option>
                    {Array.from({ length: 100 }, (_, i) => {
                      const year = new Date().getFullYear() - 18 - i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                    </select>
                  </div>
                  {fieldErrors.dateOfBirth && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{fieldErrors.dateOfBirth}</p>
                  )}
                  {form.dateOfBirth && !fieldErrors.dateOfBirth && (
                    <p className="text-green-400 text-xs mt-0.5 flex items-center gap-1">
                      <CheckCircle size={12} />
                      Valid date selected
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium mb-1 text-white">Gender</label>
                  <select
                    value={form.gender}
                    onChange={e => setField('gender', e.target.value as any)}
                    className={`w-full p-3 bg-primary-100 border-2 rounded-lg focus:ring-2 focus:ring-accent2 focus:border-accent2 focus:outline-none text-sm ${
                      fieldErrors.gender ? 'border-red-500 text-white' : form.gender === '' ? 'text-white/50 border-primary-200' : 'text-white border-primary-200'
                    }`}
                  >
                  <option value="" disabled>Select your gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="non-binary">Non-binary</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                  {fieldErrors.gender && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{fieldErrors.gender}</p>
                  )}
                  {!fieldErrors.gender && !form.gender && (
                    <p className="text-xs text-accent2 mt-1 font-medium">👆 Please select an option</p>
                  )}
                </div>
              </div>
            )}

            {/* Main Step 1: Your Interests */}
            {mainStep === 1 && subStep === 0 && (
              <div className="space-y-3">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-1 text-white">What are you into?</h2>
                  <p className="text-xs text-white/80 mb-0.5">Pick at least 3 interests. These help us create your profile and find your tribe!</p>
                  <p className="text-xs text-green-400 font-medium">🎉 Nice! Now the fun part begins...</p>
                </div>

                <div>
                  <InterestSelector
                    selectedInterests={form.interests}
                    onChange={(ints) => setField('interests', ints)}
                    allowCustom={false}
                  />
                  {fieldErrors.interests && (
                    <p className="text-red-500 text-xs mt-1 font-medium">{fieldErrors.interests}</p>
                  )}
                  {!fieldErrors.interests && (
                    <p className="text-xs text-white/60 mt-1">{form.interests.length}/3 minimum selected</p>
                  )}
                </div>
              </div>
            )}

            {mainStep === 1 && subStep === 1 && (
              <div className="space-y-3">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-1 text-white">Add a profile photo</h2>
                  <p className="text-xs text-white/70 mb-0.5">Profiles with photos get 5x more connections!</p>
                  <p className="text-xs text-accent2">📸 Show them who you are! (Or skip for now)</p>
                </div>

                {/* Step Indicator */}
                <div className="flex items-center justify-center gap-3 py-2">
                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                      !form.avatarUrl
                        ? 'bg-accent2 text-white'
                        : 'bg-green-500 text-white'
                    }`}>
                      {form.avatarUrl ? <Check size={20} /> : '1'}
                    </div>
                    <span className={`text-xs mt-1 transition-colors ${
                      !form.avatarUrl ? 'text-accent2 font-semibold' : 'text-white/60'
                    }`}>Choose Photo</span>
                  </div>

                  <div className={`h-0.5 w-12 transition-colors ${
                    form.avatarUrl ? 'bg-accent2' : 'bg-primary-200'
                  }`}></div>

                  <div className="flex flex-col items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                      form.avatarUrl
                        ? 'bg-accent2 text-white'
                        : 'bg-primary-200 text-white/40'
                    }`}>
                      2
                    </div>
                    <span className={`text-xs mt-1 transition-colors ${
                      form.avatarUrl ? 'text-accent2 font-semibold' : 'text-white/40'
                    }`}>Selected</span>
                  </div>
                </div>

                <div className="flex flex-col items-center gap-3">
                  <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full bg-primary-100 border-2 border-primary-200 flex items-center justify-center overflow-hidden">
                    {form.avatarUrl ? (
                      <img src={form.avatarUrl} className="w-full h-full object-cover" alt="preview" />
                    ) : (
                      <Camera className="text-white/40" size={48} />
                    )}
                  </div>

                  <div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/heic,image/heif,image/jpg,image/webp"
                      className="hidden"
                      onChange={onFile}
                      id="photo-upload-input"
                    />
                    <label
                      htmlFor="photo-upload-input"
                      className="cursor-pointer block"
                      onClick={() => console.log('Label clicked - file picker should open')}
                    >
                      <span className={`px-6 py-3 rounded-lg inline-flex justify-center items-center font-medium transition-all min-h-[44px] ${
                        !form.avatarUrl
                          ? 'bg-accent2 text-white hover:bg-accent2/90 ring-2 ring-accent2 ring-offset-2 ring-offset-primary'
                          : 'bg-primary-200 text-white hover:bg-primary-300'
                      }`}>
                        <Camera size={16} className="mr-2" />
                        {form.avatarUrl ? 'Change Photo' : 'Choose Photo'}
                      </span>
                    </label>
                  </div>

                  {form.avatarUrl && (
                    <button
                      type="button"
                      onClick={() => {
                        setField('avatarUrl', null);
                        setAvatarFile(null);
                      }}
                      className="text-white/60 hover:text-white text-sm min-h-[44px]"
                    >
                      Remove photo
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Main Step 2: Review & Launch */}
            {mainStep === 2 && (
              <div>
                <h2 className="text-xl sm:text-2xl font-bold mb-2 text-white">Review & Launch</h2>
                <p className="text-sm text-white/70 mb-1">Your profile is ready! Make any final edits.</p>
                <p className="text-sm text-green-400 mb-4">🚀 Woohoo! You're ready to meet your tribe!</p>

                <div className="bg-theme-cards rounded-xl p-4 sm:p-5 border border-theme-cards mb-4 shadow-card">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-white flex items-center gap-2">
                      <Sparkles size={16} className="text-theme-primary" />
                      Your Bio
                    </label>
                    <button
                      type="button"
                      onClick={handleRegenerateBio}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 rounded-full transition-all shadow-md hover:shadow-lg"
                      title="Generate a fresh bio!"
                    >
                      <Sparkles size={14} />
                      Mix it up!
                    </button>
                  </div>

                  <textarea
                    rows={4}
                    value={form.bio}
                    onChange={e => setField('bio', e.target.value)}
                    className="w-full p-3 bg-theme-background border border-gray-700 rounded-lg text-white placeholder-theme-text-secondary focus:ring-2 focus:ring-theme-primary focus:border-theme-primary focus:outline-none text-sm mb-3"
                  />

                  <p className="text-xs text-theme-text-secondary mb-2">Change personality:</p>
                  <div className="grid grid-cols-4 gap-2">
                    {personalityTemplates.map((template) => (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => handlePersonalityChange(template.id)}
                        className={`p-2 rounded-lg border-2 transition-all ${
                          selectedPersonality === template.id
                            ? 'border-theme-primary bg-theme-primary/20'
                            : 'border-gray-700 hover:border-theme-primary/50 bg-theme-cards/50'
                        }`}
                      >
                        <div className="text-2xl text-center">{template.icon}</div>
                        <div className="text-[10px] text-theme-text-secondary text-center mt-1">{template.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">
                {error}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="bg-theme-cards border-t border-primary-200/20 px-5 py-4 flex items-center justify-between">
        {mainStep === 0 && subStep === 0 ? (
          <button
            type="button"
            onClick={onLoginClick}
            className="text-accent2 hover:text-accent2/80 text-sm"
          >
            Already have an account? <span className="underline">Login</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={handleBack}
            className="px-3 sm:px-4 py-2.5 sm:py-3 text-white hover:bg-primary-100 rounded-lg min-h-[44px] text-sm sm:text-base"
          >
            Back
          </button>
        )}

        <div className="flex gap-2">
          {/* Show skip button for photo */}
          {mainStep === 1 && subStep === 1 && (
            <button
              type="button"
              onClick={handleSkip}
              className="px-3 sm:px-4 py-2.5 sm:py-3 text-white/70 hover:text-white min-h-[44px] text-sm sm:text-base"
            >
              Skip
            </button>
          )}

          <button
            type="button"
            onClick={handleNext}
            disabled={submitting}
            className="bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold disabled:opacity-50 min-w-[100px] sm:min-w-[120px] min-h-[44px] text-sm sm:text-base"
          >
            {submitting ? 'Loading...' : mainStep === 2 ? '🚀 Launch My Profile' : 'Continue'}
          </button>
        </div>
      </div>
    </div>

    {/* Welcome Modal - shown after successful registration */}
    {registrationComplete && (
      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={handleWelcomeModalClose}
        username={form.username}
      />
    )}
    </>
  );
};

export default React.memo(ProfileBuilderNew);
